import { API_BASE_URL } from '../config';

const ADMIN_EMAIL_KEY = 'admin-email';

export function getAdminCorrectedBy(): string {
  return localStorage.getItem(ADMIN_EMAIL_KEY)?.trim() || 'admin';
}

export type AdminFormSubmissionView = {
  email?: string;
  invitationId?: string;
  formCode?: string;
  submissionRound?: number;
  live?: Record<string, unknown>;
  snapshot?: Record<string, unknown>;
  snapshotRounds?: number[];
  adminCorrectedBy?: string;
  adminCorrectedAt?: string;
};

async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const base = API_BASE_URL?.trim();
  if (!base) throw new Error('API unavailable');
  const res = await fetch(`${base}${path}`, init);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export function fetchAdminFormSubmission(
  formCode: string,
  email: string,
  invitationId: string,
  submissionRound?: number
): Promise<AdminFormSubmissionView> {
  const q = new URLSearchParams({ email: email.trim(), invitationId: invitationId.trim() });
  if (submissionRound != null) q.set('submissionRound', String(submissionRound));
  return adminFetch(`/api/admin/check-submissions/forms/${encodeURIComponent(formCode)}?${q}`);
}

export function updateAdminFormSubmission(
  formCode: string,
  body: {
    email: string;
    invitationId: string;
    submissionRound?: number;
    payload: Record<string, unknown>;
    correctedBy?: string;
  }
): Promise<AdminFormSubmissionView> {
  return adminFetch(`/api/admin/check-submissions/forms/${encodeURIComponent(formCode)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...body,
      correctedBy: body.correctedBy ?? getAdminCorrectedBy(),
    }),
  });
}

/** Jackson JsonNode bean properties — not form fields (legacy bad API responses). */
const JSON_NODE_META_KEYS = new Set([
  'array',
  'bigDecimal',
  'bigInteger',
  'binary',
  'boolean',
  'containerNode',
  'double',
  'empty',
  'float',
  'floatingPointNumber',
  'int',
  'integralNumber',
  'long',
  'missingNode',
  'nodeType',
  'null',
  'number',
  'object',
  'pojo',
  'textual',
  'valueNode',
]);

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function scalarToDisplayString(v: unknown): string | null {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
    return String(v);
  }
  return null;
}

/** Extract editable string fields from admin form live / snapshot payload. */
export function extractEditableValues(live: Record<string, unknown> | undefined): Record<string, string> {
  if (!live) return {};

  const values = live.values;
  if (isPlainObject(values)) {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) {
      const s = scalarToDisplayString(v);
      if (s != null) out[k] = s;
    }
    if (Object.keys(out).length > 0) return out;
  }

  const skip = new Set([
    'email',
    'invitationId',
    'formCode',
    'currentStepId',
    'taxYear',
    ...JSON_NODE_META_KEYS,
  ]);
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(live)) {
    if (skip.has(k)) continue;
    const s = scalarToDisplayString(v);
    if (s != null) out[k] = s;
  }
  return out;
}

/** Merge edited string fields back into live payload for PUT. */
export function mergeEditableValues(
  live: Record<string, unknown>,
  edited: Record<string, string>
): Record<string, unknown> {
  const next = { ...live };
  if (isPlainObject(live.values)) {
    next.values = { ...(live.values as Record<string, unknown>), ...edited };
    return next;
  }
  for (const [k, v] of Object.entries(edited)) {
    next[k] = v;
  }
  return next;
}

export function extractSnapshotValues(snapshot: Record<string, unknown> | undefined): Record<string, string> {
  return extractEditableValues(snapshot);
}

/** Consent forms are view-only here; their PDF is the preview. FF007 is snapshot compare only. */
const ADMIN_FIELD_EDIT_UNSUPPORTED = new Set([
  'FF002',
  'FF004',
  'FF007',
  'FF010',
  'FF013',
  'FF015',
]);

/** Forms where admin PUT is supported. */
export function isAdminFormEditSupported(formCode: string): boolean {
  const code = formCode.trim().toUpperCase();
  return !ADMIN_FIELD_EDIT_UNSUPPORTED.has(code);
}
